/**
 * Preview dev-only del tab Pilas (Sprint 19a RP-04 — venta en un solo paso).
 *
 * Reusa la vista real `PilasView` con mock data y muestra el modal
 * "Vender pilas". Sin backend ni login. Gateado por `import.meta.env.DEV` en
 * App.tsx (convención #15 corolario): inaccesible en producción.
 *
 * Lo que valida el smoke visual:
 * - PilasView con sus 2 secciones (stock disponible + ventas recientes) y el
 *   botón "+ Vender pilas". Ya no hay paso "Consumir".
 * - El modal "Vender pilas" (casa + control de referencia opcional + cantidad
 *   + monto estimado). Los selectores de control/residente quedan vacíos sin
 *   backend; lo relevante es la estructura del formulario en un solo paso.
 */
import { useState } from 'react';
import { PilasView } from '../../components/tesoreria/PilasView';
import { ModalVenderPilas } from '../../components/tesoreria/ModalVenderPilas';
import type {
  LoteResumen,
  StockRecursoDisponibleResumen,
  StockRecursoRead,
} from '../../lib/api/types';
import { Button } from '../../components/ui/Button';

// ----- Mock data -----

function mockStock(
  id: string,
  estado: StockRecursoRead['estado'],
  valorVenta: number,
  extra: Partial<StockRecursoRead> = {},
): StockRecursoRead {
  return {
    id,
    tipo: 'pila',
    identificador: null,
    serial_rfid: null,
    costo_compra: 1500,
    valor_venta: valorVenta,
    ingresado_at: '2026-06-01',
    estado,
    recurso_id: null,
    recurso_identificador: null,
    consumido_at: null,
    notas: null,
    creado_at: '2026-06-01T10:00:00Z',
    actualizado_at: '2026-06-01T10:00:00Z',
    ...extra,
  };
}

const STOCK_MOCK: StockRecursoRead[] = [
  mockStock('p1', 'en_stock', 3000),
  mockStock('p2', 'en_stock', 3000),
  mockStock('p3', 'en_stock', 3500),
];

const VENTAS_MOCK: StockRecursoRead[] = [
  mockStock('v1', 'consumido', 3000, {
    consumido_at: '2026-06-15T14:00:00Z',
    recurso_id: 'cr-6834-1',
    recurso_identificador: 'CR-6834-1',
  }),
  // Venta suelta (sin control de referencia).
  mockStock('v2', 'consumido', 3000, {
    consumido_at: '2026-06-12T11:00:00Z',
    recurso_id: null,
  }),
];

const LOTES_MOCK: LoteResumen[] = [
  {
    id: 'l-6834',
    identificador: '6834',
    direccion: 'Calbucura 6834',
    sector: 'oriente',
    activo: true,
    notas: null,
    responsables_pago_nombres: [],
  },
  {
    id: 'l-6912',
    identificador: '6912',
    direccion: 'Calbucura 6912',
    sector: 'poniente',
    activo: true,
    notas: null,
    responsables_pago_nombres: [],
  },
];

const STOCK_RESUMEN_MOCK: StockRecursoDisponibleResumen[] = [
  { tipo: 'pila', cantidad_disponible: 3, valor_venta_proximo: 3000 },
];

// ----- Preview -----

export function PilasPreview() {
  const [modalAbierto, setModalAbierto] = useState(false);

  return (
    <div className="min-h-screen bg-bg-page p-6">
      <div className="max-w-4xl mx-auto space-y-5">
        <div className="bg-bg-elevated border border-line rounded-lg p-3 flex flex-wrap items-center gap-3">
          <span className="text-xs font-bold uppercase text-orange">
            Preview dev
          </span>
          <span className="text-xs text-text-secondary">
            Tab Pilas — venta en un solo paso (Sprint 19a)
          </span>
          <Button
            variant="secondary"
            size="sm"
            className="ml-auto"
            onClick={() => setModalAbierto(true)}
          >
            Abrir modal "Vender pilas"
          </Button>
        </div>

        <PilasView
          stockPila={STOCK_MOCK}
          ventasRecientes={VENTAS_MOCK}
          loading={false}
          error={null}
          onVenderPilas={() => setModalAbierto(true)}
        />
      </div>

      {modalAbierto && (
        <ModalVenderPilas
          lotes={LOTES_MOCK}
          stockResumen={STOCK_RESUMEN_MOCK}
          onCerrar={() => setModalAbierto(false)}
          onVendida={() => setModalAbierto(false)}
        />
      )}
    </div>
  );
}
